import fallbackAvatarImage from '@/assets/images/default-avatar.jpg';
import { Image } from '@/components/Image';
import { useAuthStore } from '@/stores/useAuthStore';
import i18n from '@/translations/config';
import { cn } from '@/utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IoBarChart,
  IoBarChartOutline,
  IoBusiness,
  IoBusinessOutline,
  IoCalendar,
  IoCalendarOutline,
  IoClose,
  IoEllipsisHorizontal,
  IoPeople,
  IoPeopleOutline,
  IoQrCode,
  IoQrCodeOutline,
  IoSettings,
  IoSettingsOutline,
} from 'react-icons/io5';
import { MdContacts, MdOutlineContacts } from 'react-icons/md';
import { Link, useLocation } from 'react-router-dom';

interface NavItem {
  id: string;
  to: string;
  icon: React.ReactNode;
  iconActive: React.ReactNode;
  label: string;
  role: string[];
  isExternal?: boolean;
  notificationCount?: number;
  isAction?: boolean;
}

export const BottomNav = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const location = useLocation();
  const [activeItemId, setActiveItemId] = useState<string>('');
  const [isManagementMenuOpen, setIsManagementMenuOpen] = useState(false);
  const lang = i18n.language;

  // Management navigation items
  const managementNavItems: NavItem[] = useMemo(
    () => [
      {
        id: 'settings',
        to: '/dashboard/settings',
        icon: <IoSettingsOutline className="size-6" />,
        iconActive: <IoSettings className="size-6" />,
        label: t('sidebar:settings'),
        role: ['admin', 'manager', 'user'],
      },
      {
        id: 'companies',
        to: '/dashboard/companies',
        icon: <IoBusinessOutline className="size-6" />,
        iconActive: <IoBusiness className="size-6" />,
        label: t('sidebar:companies'),
        role: ['admin'],
      },
      {
        id: 'my-company',
        to: '/dashboard/my-company',
        icon: <IoBusinessOutline className="size-6" />,
        iconActive: <IoBusiness className="size-6" />,
        label: t('sidebar:myCompany'),
        role: ['manager', 'admin'],
      },
      {
        id: 'users',
        to: '/dashboard/users',
        icon: <IoPeopleOutline className="size-6" />,
        iconActive: <IoPeople className="size-6" />,
        label: t('sidebar:users'),
        role: ['admin', 'manager'],
      },
      {
        id: 'events-management',
        to: '/dashboard/events/events-management',
        icon: <IoCalendarOutline className="size-6" />,
        iconActive: <IoCalendar className="size-6" />,
        label: t('sidebar:events'),
        role: ['admin'],
      },
      {
        id: 'qrcode',
        to: '/dashboard/qrcode',
        icon: <IoQrCodeOutline className="size-6" />,
        iconActive: <IoQrCode className="size-6" />,
        label: t('sidebar:qrcode') || 'Générateur QR Code',
        role: ['admin'],
      },
      {
        id: 'statistics',
        to: '/dashboard/statistics',
        icon: <IoBarChartOutline className="size-6" />,
        iconActive: <IoBarChart className="size-6" />,
        label: t('sidebar:statistics'),
        role: ['admin'],
      },
    ],
    [t],
  );

  // Check if user has management access
  const hasManagementAccess =
    user && (user.role === 'admin' || user.role === 'manager');

  // Navigation items (personal links)
  const navItems: NavItem[] = useMemo(
    () => [
      {
        id: 'cards',
        to: '/dashboard',
        icon: (
          <div className="relative">
            <Image
              src={user?.avatar || null}
              defaultImage={fallbackAvatarImage}
              alt="Avatar"
              className="size-6 rounded-full border border-gray-200 object-cover"
              refreshKey={user?.updated_at}
            />
          </div>
        ),
        iconActive: (
          <div className="relative">
            <Image
              src={user?.avatar || null}
              defaultImage={fallbackAvatarImage}
              alt="Avatar"
              className="size-6 rounded-full border-2 border-linko-light object-cover"
              refreshKey={user?.updated_at}
            />
          </div>
        ),
        label: t('sidebar:cards'),
        role: ['admin', 'manager', 'user'],
      },
      {
        id: 'contacts',
        to: '/dashboard/contacts',
        icon: <MdOutlineContacts className="size-6" />,
        iconActive: <MdContacts className="size-6" />,
        label: t('sidebar:contacts'),
        role: ['admin', 'manager', 'user'],
      },
      // {
      //   id: 'camera',
      //   to: '#',
      //   icon: <IoCameraOutline className="size-6" />,
      //   iconActive: <IoCamera className="size-6" />,
      //   label: 'Scan',
      //   role: ['admin', 'manager', 'user'],
      //   isAction: true,
      // },
      {
        id: 'events',
        to: '/dashboard/events',
        icon: <IoCalendarOutline className="size-6" />,
        iconActive: <IoCalendar className="size-6" />,
        label: t('sidebar:events'),
        role: ['admin', 'manager', 'user'],
        notificationCount:
          user?.meeting_requests && user?.meeting_requests > 0
            ? user?.meeting_requests
            : undefined,
      },
      ...(hasManagementAccess
        ? [
            {
              id: 'management',
              to: '#',
              icon: <IoEllipsisHorizontal className="size-6" />,
              iconActive: <IoEllipsisHorizontal className="size-6" />,
              label: t('sidebar:management') || 'More',
              role: ['admin', 'manager'],
              isAction: true,
            } as NavItem,
          ]
        : [
            {
              id: 'settings',
              to: '/dashboard/settings',
              icon: <IoSettingsOutline className="size-6" />,
              iconActive: <IoSettings className="size-6" />,
              label: t('sidebar:settings'),
              role: ['user'],
              isExternal: false,
            } as NavItem,
          ]),
    ],
    [
      t,
      user?.meeting_requests,
      user?.avatar,
      user?.updated_at,
      hasManagementAccess,
    ],
  );

  // Filter management items based on user role
  const filteredManagementItems = managementNavItems.filter(
    item => user && item.role.includes(user.role),
  );

  // Update active item when location changes
  useEffect(() => {
    const currentPath = location.pathname.replace(`/${lang}`, '');
    const activeItem = [...navItems, ...managementNavItems].find(
      item =>
        !item.isExternal &&
        !item.isAction &&
        (item.to === currentPath ||
          (currentPath === '/dashboard' && item.to === '/dashboard')),
    );
    if (activeItem) {
      setActiveItemId(activeItem.id);
    }
  }, [location.pathname, lang, navItems, managementNavItems]);

  // Filter items based on user role
  const filteredNavItems = navItems.filter(
    item => user && item.role.includes(user.role),
  );

  const handleCameraClick = () => {
    // TODO: Implement camera click
  };

  const handleManagementClick = () => {
    setIsManagementMenuOpen(!isManagementMenuOpen);
  };

  const handleNavClick = (item: NavItem) => {
    if (item.id === 'camera') {
      handleCameraClick();
      return;
    }

    if (item.id === 'management') {
      handleManagementClick();
      return;
    }

    if (isManagementMenuOpen) setIsManagementMenuOpen(false);

    setActiveItemId(item.id);
  };

  const handleManagementItemClick = (item: NavItem) => {
    setIsManagementMenuOpen(false);
    setActiveItemId(item.id);
  };

  const renderNavItem = (item: NavItem) => {
    const active = activeItemId === item.id;

    const content = (
      <motion.div
        whileTap={{ scale: 0.9 }}
        onClick={() => handleNavClick(item)}
        className="relative flex flex-col items-center justify-center"
      >
        {/* Icon container */}
        <motion.div
          className={cn(
            'relative flex size-6 items-center justify-center rounded-2xl transition-all duration-300',
            item.isAction && item.id === 'camera'
              ? 'bg-gradient-to-br from-linko-light to-linko-dark shadow-lg shadow-linko-light/25'
              : item.isAction && item.id === 'management' && active
                ? 'bg-linko-dark'
                : 'bg-transparent',
          )}
          animate={active ? { scale: 1 } : { scale: 1 }}
        >
          <div
            className={cn(
              'flex items-center justify-center transition-colors duration-300',
              active
                ? 'text-linko-light'
                : item.isAction && item.id === 'camera'
                  ? 'text-white'
                  : item.isAction && item.id === 'management'
                    ? active
                      ? 'text-white'
                      : 'text-gray-500'
                    : 'text-gray-500',
            )}
          >
            {active || item.isAction ? item.iconActive : item.icon}
          </div>
        </motion.div>

        {/* Label */}
        <span
          className={cn(
            'mt-1 text-xs font-medium transition-colors duration-300',
            active ? 'text-linko-light' : 'text-gray-500',
          )}
        >
          {item.label}
        </span>

        {/* Notification badge */}
        {item.notificationCount && item.notificationCount > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -right-1 -top-1 flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-bold text-white shadow-lg"
          >
            {item.notificationCount > 9 ? '9+' : item.notificationCount}
          </motion.div>
        )}
      </motion.div>
    );

    if (item.isAction) {
      return <div key={item.id}>{content}</div>;
    }

    return item.isExternal ? (
      <a key={item.id} href={item.to} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    ) : (
      <Link key={item.id} to={`/${lang}${item.to}`}>
        {content}
      </Link>
    );
  };

  const renderManagementItem = (item: NavItem) => {
    const active = activeItemId === item.id;

    const content = (
      <motion.div
        whileTap={{ scale: 0.98 }}
        onClick={() => handleManagementItemClick(item)}
        className="flex w-full items-center gap-4 rounded-2xl p-4 transition-all duration-200 hover:bg-gray-50 active:bg-gray-100"
      >
        {/* Icon */}
        <div
          className={cn(
            'flex size-14 items-center justify-center rounded-2xl transition-all duration-200',
            active
              ? 'bg-linko-light text-white shadow-lg shadow-linko-dark/25'
              : 'bg-gray-100 text-gray-600',
          )}
        >
          {active ? item.iconActive : item.icon}
        </div>

        {/* Label */}
        <span
          className={cn(
            'text-base font-medium transition-colors duration-200',
            active ? 'text-linko-dark' : 'text-gray-900',
          )}
        >
          {item.label}
        </span>
      </motion.div>
    );

    return item.isExternal ? (
      <a key={item.id} href={item.to} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    ) : (
      <Link key={item.id} to={`/${lang}${item.to}`}>
        {content}
      </Link>
    );
  };

  if (!user) return null;

  return (
    <>
      {/* Bottom Navigation */}
      <div
        className={cn(isManagementMenuOpen ? 'z-[60]' : 'z-50', 'lg:hidden')}
      >
        {/* Main Navigation Bar */}
        <div className=" border-t border-gray-100 bg-white/95 backdrop-blur-xl">
          <div className="flex items-center justify-around pb-6 pt-3">
            {filteredNavItems.map(item => renderNavItem(item))}
          </div>
        </div>
      </div>

      {/* Management Menu Overlay */}
      <AnimatePresence>
        {isManagementMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsManagementMenuOpen(false)}
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
            />

            {/* Management Menu */}
            <motion.div
              initial={{ y: '100%', opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: '100%', opacity: 0, scale: 0.95 }}
              transition={{
                type: 'spring' as const,
                stiffness: 300,
                damping: 30,
                opacity: { duration: 0.2 },
              }}
              className="fixed inset-x-0 bottom-0 z-50 lg:hidden"
            >
              <div className="mx-4 mb-40 max-h-[70vh] overflow-hidden rounded-3xl bg-white">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('sidebar:management')}
                  </h3>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsManagementMenuOpen(false)}
                    className="flex size-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200"
                  >
                    <IoClose className="size-5" />
                  </motion.button>
                </div>

                {/* Management Items */}
                <div className="max-h-[50vh] overflow-y-auto p-2">
                  {filteredManagementItems.map(item =>
                    renderManagementItem(item),
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
